/**
 * Reusable GraphQL fragments. Each fragment declares its transitive fragment
 * dependencies so {@link composeDocument} can assemble a complete, valid document
 * (operation + exactly the fragments it references, deduplicated).
 *
 * Every selection here mirrors field names and types in `schema.graphql`.
 */

/** A named GraphQL fragment plus the other fragments its selection set references. */
export interface Fragment {
	/** Fragment name, matching its `fragment <name> on ...` definition. */
	name: string;
	/** The fragment's SDL source. */
	sdl: string;
	/** Directly-referenced fragments; resolved transitively by {@link composeDocument}. */
	deps: Fragment[];
}

export const MediaFields: Fragment = {
	name: "MediaFields",
	deps: [],
	sdl: /* GraphQL */ `
		fragment MediaFields on MediaAsset {
			id
			key
			url
			signedUrl
			mime
			sizeBytes
			width
			height
			alt
			isPrivate
		}
	`,
};

export const CtaFields: Fragment = {
	name: "CtaFields",
	deps: [],
	sdl: /* GraphQL */ `
		fragment CtaFields on CtaLink {
			label
			href
			routeKey
			external
		}
	`,
};

export const PublishableFields: Fragment = {
	name: "PublishableFields",
	deps: [],
	sdl: /* GraphQL */ `
		fragment PublishableFields on Publishable {
			documentId
			slug
			status
			locale
			isDemo
			publishedAt
		}
	`,
};

export const SeoFields: Fragment = {
	name: "SeoFields",
	deps: [MediaFields],
	sdl: /* GraphQL */ `
		fragment SeoFields on SeoMeta {
			title
			description
			canonicalUrl
			noindex
			hreflang {
				locale
				url
			}
			ogImage {
				...MediaFields
			}
			structuredData
		}
	`,
};

export const NavItemFields: Fragment = {
	name: "NavItemFields",
	deps: [],
	sdl: /* GraphQL */ `
		fragment NavItemFields on NavigationItem {
			label
			routeKey
			href
		}
	`,
};

export const ServiceCardFields: Fragment = {
	name: "ServiceCardFields",
	deps: [PublishableFields, MediaFields],
	sdl: /* GraphQL */ `
		fragment ServiceCardFields on Service {
			id
			...PublishableFields
			title
			usp
			summary
			materialStreams
			category {
				title
			}
			heroImage {
				...MediaFields
			}
		}
	`,
};

export const CompanyCardFields: Fragment = {
	name: "CompanyCardFields",
	deps: [PublishableFields, MediaFields],
	sdl: /* GraphQL */ `
		fragment CompanyCardFields on Company {
			id
			...PublishableFields
			name
			legalName
			description
			website
			logo {
				...MediaFields
			}
		}
	`,
};

export const CaseStudyCardFields: Fragment = {
	name: "CaseStudyCardFields",
	deps: [PublishableFields, MediaFields],
	sdl: /* GraphQL */ `
		fragment CaseStudyCardFields on CaseStudy {
			id
			...PublishableFields
			title
			tag
			outcome
			metrics {
				label
				value
			}
			company {
				name
			}
			heroImage {
				...MediaFields
			}
		}
	`,
};

export const ArticleCardFields: Fragment = {
	name: "ArticleCardFields",
	deps: [PublishableFields, MediaFields],
	sdl: /* GraphQL */ `
		fragment ArticleCardFields on Article {
			id
			...PublishableFields
			title
			excerpt
			authorName
			tags
			readingMinutes
			heroImage {
				...MediaFields
			}
		}
	`,
};

export const FormDefinitionFields: Fragment = {
	name: "FormDefinitionFields",
	deps: [],
	sdl: /* GraphQL */ `
		fragment FormDefinitionFields on FormDefinition {
			id
			key
			title
			submitLabel
			successMessage
			leadSource
			fields {
				name
				label
				type
				required
				validationKey
				options {
					label
					value
				}
			}
			consents {
				kind
				version
				label
				required
			}
		}
	`,
};

export const PageBlocks: Fragment = {
	name: "PageBlocks",
	deps: [MediaFields, CtaFields, ServiceCardFields, CaseStudyCardFields, FormDefinitionFields],
	sdl: /* GraphQL */ `
		fragment PageBlocks on PageBlock {
			__typename
			... on HeroBlock {
				heading
				subheading
				image {
					...MediaFields
				}
				cta {
					...CtaFields
				}
			}
			... on RichTextBlock {
				html
			}
			... on ServiceGridBlock {
				services {
					...ServiceCardFields
				}
			}
			... on CaseStudyListBlock {
				caseStudies {
					...CaseStudyCardFields
				}
			}
			... on ProcessStepsBlock {
				steps {
					title
					description
				}
			}
			... on FormBlock {
				form {
					...FormDefinitionFields
				}
			}
			... on CtaBlock {
				heading
				# Aliased: HeroBlock.cta is CtaLink, CtaBlock.cta is CtaLink!, and two
				# same-named fields with different nullability make the selection invalid.
				action: cta {
					...CtaFields
				}
			}
		}
	`,
};

export const ServiceFull: Fragment = {
	name: "ServiceFull",
	deps: [PublishableFields, MediaFields, CompanyCardFields, CaseStudyCardFields, SeoFields],
	sdl: /* GraphQL */ `
		fragment ServiceFull on Service {
			id
			...PublishableFields
			title
			usp
			summary
			body
			materialStreams
			heroImage {
				...MediaFields
			}
			category {
				id
				documentId
				slug
				title
				summary
				icon
				order
			}
			companies {
				...CompanyCardFields
			}
			caseStudies {
				...CaseStudyCardFields
			}
			seo {
				...SeoFields
			}
		}
	`,
};

export const CompanyFull: Fragment = {
	name: "CompanyFull",
	deps: [PublishableFields, MediaFields, ServiceCardFields, SeoFields],
	sdl: /* GraphQL */ `
		fragment CompanyFull on Company {
			id
			...PublishableFields
			name
			legalName
			description
			website
			logo {
				...MediaFields
			}
			services {
				...ServiceCardFields
			}
			seo {
				...SeoFields
			}
		}
	`,
};

export const CaseStudyFull: Fragment = {
	name: "CaseStudyFull",
	deps: [PublishableFields, MediaFields, ServiceCardFields, CompanyCardFields, SeoFields],
	sdl: /* GraphQL */ `
		fragment CaseStudyFull on CaseStudy {
			id
			...PublishableFields
			title
			challenge
			solution
			outcome
			metrics {
				label
				value
			}
			heroImage {
				...MediaFields
			}
			services {
				...ServiceCardFields
			}
			company {
				...CompanyCardFields
			}
			seo {
				...SeoFields
			}
		}
	`,
};

export const ArticleFull: Fragment = {
	name: "ArticleFull",
	deps: [PublishableFields, MediaFields, SeoFields],
	sdl: /* GraphQL */ `
		fragment ArticleFull on Article {
			id
			...PublishableFields
			title
			excerpt
			body
			authorName
			tags
			readingMinutes
			heroImage {
				...MediaFields
			}
			seo {
				...SeoFields
			}
		}
	`,
};

export const ResourceFull: Fragment = {
	name: "ResourceFull",
	deps: [PublishableFields, MediaFields, SeoFields],
	sdl: /* GraphQL */ `
		fragment ResourceFull on Resource {
			id
			...PublishableFields
			title
			description
			gated
			kind
			file {
				...MediaFields
			}
			seo {
				...SeoFields
			}
		}
	`,
};

/**
 * Webinar list cards: no registration form, recording or SEO. The site's list
 * reads only these fields, so there is no reason to fetch the rest.
 */
export const WebinarCardFields: Fragment = {
	name: "WebinarCardFields",
	deps: [PublishableFields],
	sdl: /* GraphQL */ `
		fragment WebinarCardFields on Webinar {
			id
			...PublishableFields
			title
			description
			startsAt
			endsAt
		}
	`,
};

export const WebinarFull: Fragment = {
	name: "WebinarFull",
	deps: [PublishableFields, MediaFields, FormDefinitionFields, SeoFields],
	sdl: /* GraphQL */ `
		fragment WebinarFull on Webinar {
			id
			...PublishableFields
			title
			description
			startsAt
			endsAt
			registrationForm {
				...FormDefinitionFields
			}
			recording {
				...MediaFields
			}
			seo {
				...SeoFields
			}
		}
	`,
};

/** Collect a fragment plus all transitive deps, deduplicated, preserving order. */
function collect(fragments: Fragment[]): Fragment[] {
	const seen = new Set<string>();
	const out: Fragment[] = [];
	const visit = (fragment: Fragment): void => {
		for (const dep of fragment.deps) visit(dep);
		if (!seen.has(fragment.name)) {
			seen.add(fragment.name);
			out.push(fragment);
		}
	};
	for (const fragment of fragments) visit(fragment);
	return out;
}

/**
 * Assemble a complete operation document from `operation` plus `roots` and all their
 * transitive fragment dependencies, deduplicated and dependency-ordered.
 *
 * @param operation The query/mutation SDL that references the fragments.
 * @param roots Top-level fragments used by the operation.
 * @returns The operation followed by every required fragment definition.
 */
export function composeDocument(operation: string, roots: Fragment[]): string {
	const fragments = collect(roots)
		.map((fragment) => fragment.sdl.trim())
		.join("\n\n");
	return `${operation.trim()}\n\n${fragments}\n`;
}
